import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame } from '../state/store'
import { clearSave } from '../state/persist'
import { fmt } from '../engine/decimal'
import { nextLevelCost, letterOutput, type LetterState } from '../engine/formulas'
import { LETTERS, LETTERS_BY_ID } from '../config/letters'
import { GLOBAL, type Rarity } from '../config/global'
import { MasteryCard } from './MasteryCard'

const RARITY_CLASS: Record<Rarity, string> = {
  Common: 'text-rarity-common border-rarity-common/40',
  Uncommon: 'text-rarity-uncommon border-rarity-uncommon/40',
  Rare: 'text-rarity-rare border-rarity-rare/40',
  Legend: 'text-rarity-legend border-rarity-legend/40',
}

const COMPACT_THRESHOLD = 10

function visibleCount(letters: LetterState[]): number {
  let owned = 0
  for (const l of letters) if (l.level > 0) owned++
  return Math.min(LETTERS.length, owned + 2)
}

type BuyMode = 'x1' | 'x10' | 'MAX'

export function LetterShop() {
  const letters = useGame((s) => s.letters)
  const ink = useGame((s) => s.ink)
  const mastery = useGame((s) => s.mastery)
  const buyLetter = useGame((s) => s.buyLetter)
  const levelUp = useGame((s) => s.levelUp)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [buyMode, setBuyMode] = useState<BuyMode>('x1')
  const [forceCompact, setForceCompact] = useState<boolean | null>(null)
  const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  const max = visibleCount(letters)
  const ownedCount = letters.filter((l) => l.level > 0).length
  const isCompact = forceCompact ?? ownedCount >= COMPACT_THRESHOLD

  const cycleBuyMode = () => {
    setBuyMode((prev) => (prev === 'x1' ? 'x10' : prev === 'x10' ? 'MAX' : 'x1'))
  }

  const getBuyCount = useCallback(() => {
    if (buyMode === 'MAX') return 999
    return buyMode === 'x10' ? 10 : 1
  }, [buyMode])

  const startHold = useCallback((id: number) => {
    levelUp(id, getBuyCount())
    holdTimer.current = setInterval(() => levelUp(id, getBuyCount()), 150)
  }, [levelUp, getBuyCount])

  const stopHold = useCallback(() => {
    if (holdTimer.current) {
      clearInterval(holdTimer.current)
      holdTimer.current = null
    }
  }, [])

  const addDebugInk = () => {
    useGame.setState((s) => ({ ink: s.ink.add(1000) }))
  }

  const resetGame = () => {
    useGame.getState().hardReset()
    clearSave()
    window.location.reload()
  }

  return (
    <section className="rounded-lg border border-ink-border bg-ink-panel p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-ink-teal">
          Letter Shop
        </h2>
        <div className="flex items-center gap-2">
          {import.meta.env.DEV && (
            <>
              <button
                onClick={addDebugInk}
                className="rounded border border-ink-border px-2 py-0.5 text-[10px] text-ink-muted hover:border-ink-tealdim hover:text-ink-teal"
              >
                +1000 ink
              </button>
              <button
                onClick={resetGame}
                className="rounded border border-red-400 px-2 py-0.5 text-[10px] text-red-400 hover:bg-red-400/10"
              >
                Reset
              </button>
            </>
          )}
          <button
            onClick={() => setForceCompact(prev => prev === null ? !isCompact : !prev)}
            className="rounded border border-ink-border px-2 py-0.5 text-[10px] text-ink-muted hover:border-ink-tealdim hover:text-ink-teal"
          >
            {isCompact ? 'Grid' : 'Compact'}
          </button>
          <button
            onClick={cycleBuyMode}
            className="rounded border border-ink-border px-2 py-0.5 text-[10px] text-ink-muted hover:border-ink-tealdim hover:text-ink-teal"
          >
            {buyMode}
          </button>
        </div>
      </div>

      {isCompact ? (
        <div className="grid grid-cols-7 sm:grid-cols-9 md:grid-cols-11 gap-1.5">
          {letters.slice(0, max).map((ls) => {
            const def = LETTERS_BY_ID[ls.id]
            const owned = ls.level > 0
            const isPassive = owned && ls.level >= GLOBAL.passiveUnlockLevel
            const cost = nextLevelCost(def, ls.level)
            const affordable = ink.gte(cost)
            const isExpanded = expandedId === ls.id

            return (
              <motion.div key={ls.id} className="relative">
                <button
                  disabled={!affordable && !owned}
                  onClick={() => buyLetter(ls.id)}
                  onMouseDown={owned ? () => startHold(ls.id) : undefined}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={owned ? () => startHold(ls.id) : undefined}
                  onTouchEnd={stopHold}
                  className={`w-full aspect-square flex flex-col items-center justify-center rounded border text-xs font-bold transition ${
                    isPassive
                      ? `${RARITY_CLASS[def.rarity]} bg-ink-teal/15 animate-passive-glow`
                      : owned
                        ? `${RARITY_CLASS[def.rarity]} bg-ink-panel shadow-sm`
                        : affordable
                          ? 'border-ink-tealdim text-ink-teal bg-ink-bg hover:shadow-glow border-dashed'
                          : 'border-ink-border text-ink-muted opacity-40'
                  } ${ls.capital ? 'ring-1 ring-amber-400' : ''}`}
                >
                  <span className="text-base">
                    {ls.capital ? def.letter : (owned ? def.letter : def.letter.toLowerCase())}
                  </span>
                  {owned && (
                    <span className="text-[8px] text-ink-muted leading-none">{ls.level}</span>
                  )}
                  {!owned && affordable && (
                    <span className="text-[8px] text-ink-teal leading-none">buy</span>
                  )}
                </button>

                {owned && (
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpandedId(isExpanded ? null : ls.id)
                    }}
                    className="absolute -top-1 -right-1 h-4 w-4 rounded-full border border-ink-tealdim bg-ink-bg text-[8px] text-ink-teal hover:shadow-glow flex items-center justify-center"
                  >
                    i
                  </motion.button>
                )}

                <AnimatePresence>
                  {isExpanded && owned && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full z-20 mt-1"
                    >
                      <CompactDetail
                        ls={ls}
                        def={def}
                        cost={cost}
                        affordable={affordable}
                        onBuy={() => levelUp(ls.id, getBuyCount())}
                        onClose={() => setExpandedId(null)}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 items-stretch">
          {letters.slice(0, max).map((ls) => {
            const def = LETTERS_BY_ID[ls.id]
            const owned = ls.level > 0
            const cost = nextLevelCost(def, ls.level)
            const affordable = ink.gte(cost)
            const out = letterOutput(ls)
            const m = mastery[ls.id]
            const milestoneCount = m
              ? [m.milestoneLexicographer, m.milestoneLengthy, m.milestoneRaritySeeker, m.milestoneChainLink, m.milestoneSwift].filter(Boolean).length
              : 0
            const isExpanded = expandedId === ls.id

            return (
              <motion.div
                key={ls.id}
                className="relative flex"
              >
                <button
                  disabled={!affordable && !owned}
                  onClick={() => buyLetter(ls.id)}
                  className={`w-full h-full flex items-center gap-3 rounded-md border p-3 pr-8 text-left transition ${
                    owned && ls.level >= GLOBAL.passiveUnlockLevel
                      ? 'border-ink-teal/50 bg-ink-panel animate-passive-glow'
                      : owned
                        ? 'border-ink-tealdim bg-ink-panel shadow-sm'
                        : affordable
                          ? 'border-ink-tealdim border-dashed bg-ink-bg hover:shadow-glow text-ink-teal'
                          : 'border-ink-border opacity-40'
                  }`}
                >
                  <span
                    className={`chip h-11 w-11 text-xl ${RARITY_CLASS[def.rarity]} ${
                      ls.capital ? 'font-extrabold' : ''
                    }`}
                  >
                    [{ls.capital ? def.letter : def.letter.toLowerCase()}]
                  </span>
                  <span className="flex-1">
                    <span className="flex justify-between text-sm">
                      <span className="text-ink-text">
                        {def.letter}
                        {owned && (
                          <span className="text-ink-muted"> · Lv{ls.level}</span>
                        )}
                      </span>
                      <span className="text-[10px] uppercase text-ink-muted">
                        {def.rarity}
                      </span>
                    </span>
                    <span className="mt-0.5 flex justify-between text-xs">
                      <span className={owned ? 'text-ink-teal' : 'text-ink-tealdim'}>
                        {owned ? `${fmt(out)}/s` : 'Buy'}
                      </span>
                      <span
                        className={affordable ? 'text-ink-text' : 'text-ink-muted'}
                      >
                        {fmt(cost)} Ink
                      </span>
                    </span>
                    {def.trait && owned && (
                      <span className="mt-0.5 block text-[10px] text-ink-muted">
                        trait: {def.trait}
                      </span>
                    )}
                    {owned && !ls.capital && (
                      <span className="mt-1 flex gap-0.5">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <span
                            key={i}
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                              i < milestoneCount ? 'bg-ink-teal' : 'bg-ink-border'
                            }`}
                          />
                        ))}
                      </span>
                    )}
                  </span>
                </button>
                {owned && (
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpandedId(isExpanded ? null : ls.id)
                    }}
                    className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full border border-ink-tealdim bg-ink-bg text-[10px] font-bold text-ink-teal hover:shadow-glow flex items-center justify-center"
                  >
                    i
                  </motion.button>
                )}
                {owned && (
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onMouseDown={() => startHold(ls.id)}
                    onMouseUp={stopHold}
                    onMouseLeave={stopHold}
                    onTouchStart={() => startHold(ls.id)}
                    onTouchEnd={stopHold}
                    disabled={!affordable}
                    className="absolute bottom-1.5 right-1.5 rounded border border-ink-tealdim px-1.5 py-1 text-[11px] font-bold text-ink-teal hover:shadow-glow disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ↑
                  </motion.button>
                )}
                {isExpanded && owned && (
                  <div className="absolute left-0 right-0 top-full z-10">
                    <MasteryCard letter={ls} />
                  </div>
                )}
              </motion.div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function CompactDetail({ ls, def, cost, affordable, onBuy, onClose }: {
  ls: LetterState
  def: typeof LETTERS[number]
  cost: ReturnType<typeof nextLevelCost>
  affordable: boolean
  onBuy: () => void
  onClose: () => void
}) {
  const out = letterOutput(ls)
  const m = useGame.getState().mastery[ls.id]
  const milestoneCount = m
    ? [m.milestoneLexicographer, m.milestoneLengthy, m.milestoneRaritySeeker, m.milestoneChainLink, m.milestoneSwift].filter(Boolean).length
    : 0

  return (
    <div className="w-48 rounded-md border border-ink-border bg-ink-panel p-2.5 shadow-lg">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-bold text-ink-text">{def.letter} · Lv{ls.level}</span>
        <button onClick={onClose} className="text-[10px] text-ink-muted hover:text-ink-text">✕</button>
      </div>
      <div className="space-y-1 text-[10px]">
        <div className="flex justify-between">
          <span className="text-ink-teal">{fmt(out)}/s</span>
          <span className="text-[10px] uppercase text-ink-muted">{def.rarity}</span>
        </div>
        {def.trait && <div className="text-ink-muted">trait: {def.trait}</div>}
        <div className="flex justify-between">
          <span className={affordable ? 'text-ink-text' : 'text-ink-muted'}>{fmt(cost)} Ink</span>
          <span className="flex gap-0.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`inline-block h-1 w-1 rounded-full ${i < milestoneCount ? 'bg-ink-teal' : 'bg-ink-border'}`} />
            ))}
          </span>
        </div>
      </div>
      <button
        onClick={onBuy}
        disabled={!affordable}
        className="mt-2 w-full rounded border border-ink-tealdim py-1 text-[10px] font-bold text-ink-teal hover:shadow-glow disabled:opacity-40"
      >
        Upgrade
      </button>
    </div>
  )
}
